<script setup lang='ts'>
import * as echarts from 'echarts/core.js'
import {
  BarChart,
} from 'echarts/charts.js'
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  VisualMapComponent,
} from 'echarts/components.js'
import { LabelLayout, UniversalTransition } from 'echarts/features.js'
import { CanvasRenderer } from 'echarts/renderers.js'
import type {
  // 系列类型的定义后缀都为 SeriesOption
  BarSeriesOption,
  LineSeriesOption,
} from 'echarts/charts.js'

import type {
  DatasetComponentOption,
  GridComponentOption,
  TitleComponentOption,
  TooltipComponentOption,
} from 'echarts/components'
import { ModelYear, logicReputationModel, logicReputationStatistics } from '~/logic/reputation/detail'
import { useGetRouteParam } from '~/shared/function'

export type ECOption = echarts.ComposeOption<
  | BarSeriesOption
  | LineSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | GridComponentOption
  | DatasetComponentOption
>

const props = withDefaults(defineProps<{
  modelYears?: string[]
}>(), {
  modelYears: () => [],
})
const loading = ref(true)

// 注册必须的组件
echarts.use([
  VisualMapComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  BarChart,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
])

// 车型信息
const {
  currentYear,
  currentYearModels,
  getModelList,
  getModelName,
} = logicReputationModel()

// statistics
const {
  modelId,
  complaintCountStatistics,
  complaintIssueStatistics,
  getIssueStat,
  getComplaintCountStat,
  initHandle: statisticsInitHandle,
} = logicReputationStatistics()

const statisticTitle = computed(() => {
  let title = '全部年款统计'
  if (currentYear.value && modelId.value) {
    const modelInfo = getModelName(modelId.value)
    if (modelInfo && modelInfo.carModelName)
      title = `${modelInfo.carModelName}统计`
  }
  else if (currentYear.value && currentYear.value !== ModelYear.all) {
    title = `${currentYear.value}年款统计`
  }
  return title
})
const chartMainOption = computed<ECOption>(() => {
  return {
    type: 'bar',
    title: {
      text: '投诉量统计', // 全部年款统计
      textAlign: 'center',
      textStyle: {
        width: 500,
      },
      padding: [16, 0, 0, 0],
      left: '50%',
    },
    // 此系列自己的调色盘。
    color: ['#478AFA'],
    tooltip: {},
    xAxis: {
      type: 'category',
      axisTick: {
        show: false,
      },
      axisLabel: {
        rotate: 50,
      },
      data: complaintCountStatistics.value.year,

    },
    yAxis: {},
    series: [
      {
        name: '投诉量',
        type: 'bar',
        data: complaintCountStatistics.value.data,
      },
    ],
  }
})
const issueChartOption = computed<ECOption>(() => ({
  type: 'bar',
  title: {
    text: '系统故障统计',
    textAlign: 'center',
    textStyle: {
      width: 500,
    },
    padding: [16, 0, 0, 0],
    left: '50%',
  },
  // 此系列自己的调色盘。
  color: ['#478AFA'],
  visualMap: {
    type: 'piecewise',
    orient: 'horizontal',
    left: 20,
    bottom: 10,
    textGap: 5,
    itemGap: 24,
    align: 'left',
    textStyle: {
      width: 100,
      height: 30,
      fontSize: 12,
      lineHeight: 100,
    },
    pieces: [
      { gte: 301, label: '301以上', color: '#4818CB' },
      { gte: 151, lte: 300, label: '151-300', color: '#0B05B3' },
      { gte: 101, lte: 150, label: '101-150', color: '#5C1AFF' },
      { gte: 51, lte: 100, label: '51-100', color: '#6956F7' },
      { gte: 21, lte: 50, label: '21-50', color: '#4959C9' },
      { gte: 11, lte: 20, label: '11-20', color: '#2752F2' },
      { gte: 6, lte: 10, label: '6-10', color: '#478FFA' },
      { gte: 1, lte: 5, label: '1-5', color: '#478FFA' }, // (10, 200]
    ],
  },
  xAxis: {
    type: 'category',
    axisTick: {
      show: false,
    },
    data: complaintIssueStatistics.value.keys,
  },
  yAxis: {
    name: '故障量',
    nameLocation: 'middle',
    nameGap: 35,
  },
  tooltip: {
    formatter: '故障量:{c}个',
  },
  series: [
    {
      name: '投诉量',
      type: 'bar',
      colorBy: 'data',
      data: complaintIssueStatistics.value.counts,
      label: {
        show: true,
        formatter: '{c}个',
      },
      labelLayout(params) {
        return {
          align: 'center',
          y: params.rect.y - 20,
        }
      },

    },
  ],
}))

// 投诉量统计组件
const mainChartRef = ref()
const mainChart = shallowRef<echarts.ECharts>()
// 系统故障统计组件
const issueChartRef = ref()
const issueChart = shallowRef<echarts.ECharts>()

// 获取新的统计数据
async function updateBaseData() {
  loading.value = true
  await Promise.all([getIssueStat(), getComplaintCountStat()]).finally(() => {
    loading.value = false
  })
}

// echarts 更新
function updateChart(type: 'main' | 'issue' = 'main') {
  if (mainChart.value && type === 'main')
    mainChart.value.setOption(chartMainOption.value)

  else if (issueChart.value && type === 'issue')
    issueChart.value.setOption(issueChartOption.value)
}

// 监听统计数据变化
watch(chartMainOption, () => {
  updateChart('main')
})
watch(issueChartOption, () => {
  updateChart('issue')
})

// 监听年份和车系变化
watchEffect(() => {
  statisticsInitHandle(currentYear.value)
  updateBaseData()
})

const seriesId = useGetRouteParam('seriesId', '')
function initHandle() {
  seriesId.value && getModelList(seriesId.value)
}
initHandle()
// 组件挂载完成,初始化echarts组件
onMounted(() => {
  nextTick(() => {
    mainChart.value = echarts.init(mainChartRef.value)
    issueChart.value = echarts.init(issueChartRef.value)

    updateChart('main')
    updateChart('issue')
  })
})
</script>

<template>
  <div class="border-1 border-slate-2 text-3.5 rounded-md">
    <div class="flex items-center justify-between py-3 pr-6 pl-4 bg-tips bg-slate-1">
      <div class="flex-grow min-w-0 overflow-hidden mr-6">
        <NTabs v-model:value="currentYear" title="滚动鼠标可左右滚动">
          <NTab v-for="v in props.modelYears" :key="v" :label="String(v)" :name="v" />
        </NTabs>
      </div>
      <div>
        <NSelect
          v-model:value="modelId"
          :consistent-menu-width="false"
          filterable
          clearable placeholder="请选择款型"
          :options="currentYearModels.map(v => ({ value: v.id, label: v.carModelName }))"
        />
      </div>
    </div>
    <div class="p-xl">
      <div class="title text-center font-bold pb-6 text-6">
        {{ statisticTitle }}
      </div>
      <div class="flex justify-between mb-6">
        <div ref="mainChartRef" class="chart-main border-1 border-slate-2 rounded-md" />
        <div class="chart-tips flex-shrink-0 p-4 border-1 border-slate-2 rounded-1.5">
          <div class="text-center border-b-1 border-slate-2 pb-4 mb-4 font-bold text-4">
            典型故障
          </div>
          <div>
            <BasicLabel
              :is-show-popover="false"
              :is-show-more="false"
              :data-list="complaintIssueStatistics.labels"
              line-count="9" placement="right"
            />
          </div>
        </div>
      </div>
      <div ref="issueChartRef" class="chart-system border-1 border-slate-2 rounded-1.5" />
    </div>
  </div>
</template>

<style lang='scss' scoped>
.chart-main {
  width: 536px;
  height: 340px;
}

.chart-tips {
  width: 220px;
}

.chart-system {
  width: 100%;

  height: 312px;
}

:deep(*) {
  .el-loading-mask{
    z-index: 99;
  }
  .el-tabs__nav-wrap {
    &::after {
      all: initial;
    }
  }

  .el-tabs__active-bar {
    display: none;
  }

  .el-tabs__header {
    margin: 0;
  }

  .el-tabs__item {
    color: var(--el-color-info);
    font-size: 14px;
    font-weight: bold;

    &.is-active {
      color: var(--el-text-color-primary);
    }
  }

  .el-tabs__nav-next,
  .el-tabs__nav-prev {
    font-size: 16px;
    color: var(--el-color-primary);

    &.is-disabled {
      color: var(--el-color-info);
    }
  }
}
</style>
